import React from 'react';
import { SET_CATEGORIES, getDefaultParamsForCategory } from '../../constants/blockTypes';
import { colors as theme } from '../../theme';

const brandColors = {
  gold: theme.gold,
  darkGray: theme.surface1,
  mediumGray: theme.surface2,
  lightGray: theme.surface3,
  silver: theme.textMuted,
  white: theme.text,
};

/**
 * Block Configuration Panel
 *
 * Renders dynamic configuration inputs based on the selected set category.
 * Each category has specific params (e.g., drop set has dropsCount, dropPercentEach).
 *
 * Props:
 * - category: The set_category id
 * - params: Current category_params object
 * - onChange: Callback when params change (receives updated params object)
 * - disabled: Boolean to disable inputs
 */
const BlockConfigPanel = ({
  category = 'normal',
  params = {},
  onChange,
  disabled = false,
}) => {
  const categoryConfig = SET_CATEGORIES[category];
  const hasParams = categoryConfig && categoryConfig.params && categoryConfig.params.length > 0;

  // Ensure default params are set (must be before early return to follow rules of hooks)
  React.useEffect(() => {
    if (!hasParams) return;
    const defaults = getDefaultParamsForCategory(category);
    const needsDefaults = Object.keys(defaults).some(key => params[key] === undefined);
    if (needsDefaults) {
      onChange({ ...defaults, ...params });
    }
  }, [category, hasParams, params, onChange]);

  if (!hasParams) {
    return null;
  }

  const handleParamChange = (key, value) => {
    const newParams = {
      ...getDefaultParamsForCategory(category),
      ...params,
      [key]: value,
    };
    onChange(newParams);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.headerText}>{categoryConfig.label} Settings</span>
      </div>

      <div style={styles.paramsGrid}>
        {categoryConfig.params.map((param) => {
          const currentValue = params[param.key] ?? param.default;

          return (
            <div key={param.key} style={styles.paramItem}>
              <label style={styles.label}>{param.label}</label>

              {param.type === 'number' && (
                <input
                  type="number"
                  value={currentValue}
                  onChange={(e) => handleParamChange(param.key, parseFloat(e.target.value) || 0)}
                  min={param.min}
                  max={param.max}
                  disabled={disabled}
                  style={styles.input}
                />
              )}

              {param.type === 'text' && (
                <input
                  type="text"
                  value={currentValue}
                  onChange={(e) => handleParamChange(param.key, e.target.value)}
                  disabled={disabled}
                  style={styles.input}
                />
              )}

              {param.type === 'boolean' && (
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={currentValue}
                    onChange={(e) => handleParamChange(param.key, e.target.checked)}
                    disabled={disabled}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>
                    {currentValue ? 'Yes' : 'No'}
                  </span>
                </label>
              )}

              {param.type === 'select' && param.options && (
                <select
                  value={currentValue}
                  onChange={(e) => handleParamChange(param.key, e.target.value)}
                  disabled={disabled}
                  style={styles.select}
                >
                  {param.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Compact param display for showing configured values
 */
export const ParamsDisplay = ({ category, params }) => {
  const categoryConfig = SET_CATEGORIES[category];
  if (!categoryConfig || !categoryConfig.params || categoryConfig.params.length === 0) {
    return null;
  }

  const displayItems = categoryConfig.params
    .filter(param => params[param.key] !== undefined)
    .map(param => ({
      label: param.label,
      value: params[param.key],
      type: param.type,
    }));

  if (displayItems.length === 0) return null;

  return (
    <div style={styles.paramsDisplay}>
      {displayItems.map((item, idx) => (
        <span key={idx} style={styles.paramTag}>
          {item.label}: {item.type === 'boolean' ? (item.value ? 'Yes' : 'No') : item.value}
        </span>
      ))}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: brandColors.darkGray,
    borderRadius: '8px',
    padding: '12px',
    marginTop: '12px',
    border: `1px solid ${brandColors.lightGray}`,
  },
  header: {
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${brandColors.lightGray}`,
  },
  headerText: {
    color: brandColors.gold,
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  paramsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '12px',
  },
  paramItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    color: brandColors.silver,
    fontSize: '11px',
    fontWeight: '500',
  },
  input: {
    padding: '8px 10px',
    backgroundColor: brandColors.mediumGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '14px',
    outline: 'none',
  },
  select: {
    padding: '8px 10px',
    backgroundColor: brandColors.mediumGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: brandColors.gold,
  },
  checkboxText: {
    color: brandColors.white,
    fontSize: '14px',
  },
  paramsDisplay: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '8px',
  },
  paramTag: {
    display: 'inline-block',
    padding: '2px 8px',
    backgroundColor: brandColors.lightGray,
    color: brandColors.silver,
    borderRadius: '4px',
    fontSize: '11px',
  },
};

export default BlockConfigPanel;
